import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Autocomplete,
    Grid,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TextField,
    Typography,
} from "@mui/material";
import PageHeader from "../../components/PageHeader";
import FormSection from "../../components/ui/FormSection";
import ChoiceChip from "../../components/ui/ChoiceChip";
import Price from "../../components/ui/Price";
import Eyebrow from "../../components/ui/Eyebrow";
import ConfirmDialog from "../../components/ConfirmDialog";
import { SpecItem, SpecList } from "../../components/ui/SpecList";
import { DateTimePicker } from "@mui/x-date-pickers";
import { FaCartPlus, FaPlus, FaTrash } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import CustomContainer from "../../components/CustomContainer";
import LoadingComponent from "../../components/LoadingComponent";
import { useModal } from "../../contexts/ModalProvider";
import {
    getCategories,
    getComforts,
    getExternalFinishings,
    getFormats,
    getPlatings,
} from "../../helpers/httpRequests/product_props";
import { getProductDetails } from "../../helpers/httpRequests/products";
import { createOrder } from "../../helpers/httpRequests/orders";
import {
    formatOptions,
    formatOptionsWithPrice,
    isOutsideDeliveryHours,
    isWeekend,
    moneyFormat,
    nextBusinessDay,
} from "../../utils/functions";
import { getServices } from "../../helpers/httpRequests/services";
import { metalList } from "../../utils/variables";
import SelectStonesModal from "./Components/SelectStonesModal";
import appToast from "../../components/appToast";

const RING_SIZES = Array.from({ length: 25 }, (_, i) => i + 10);
const DELIVERY_MIN = dayjs().hour(8).minute(30).second(0);
const DELIVERY_MAX = dayjs().hour(17).minute(0).second(0);

const priceByMaterial = {
    1: "high_gold_price",
    2: "low_gold_price",
    3: "silver_price",
};

function normalizeName(value) {
    return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function getPieceKind(product) {
    const name = normalizeName(product?.category_name);
    if (name.includes("alian")) return "alliance";
    if (name.includes("anel")) return "ring";
    return "other";
}

function optionLabel(options, value) {
    const found = options?.find((opt) => String(opt.value) === String(value));
    return found?.label || "";
}

function withUnit(value, unit) {
    if (value == null || value === "") return "";
    return `${value} ${unit}`;
}

function parseWeight(value) {
    if (value == null || value === "") return null;
    const n = Number(String(value).replace(",", "."));
    return Number.isFinite(n) ? n : null;
}

function formatGrams(value) {
    const n = parseWeight(value);
    if (n == null) return "";
    return String(Number(n.toFixed(3)));
}

export default function OrderSpecifications() {
    const { setImage, handleOpen } = useModal();
    const { id } = useParams();
    const navigate = useNavigate();
    const minDate = useMemo(() => nextBusinessDay(dayjs(), 2), []);

    const [selectedMetal, setSelectedMetal] = useState(1);
    const [extras, setExtras] = useState([]);
    const [stones, setStones] = useState([]);
    const [stoneOpen, setStoneOpen] = useState(false);
    const [pair, setPair] = useState(true);
    const [ringSize1, setRingSize1] = useState("");
    const [ringSize2, setRingSize2] = useState("");
    const [engraving1, setEngraving1] = useState("");
    const [engraving2, setEngraving2] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(minDate);
    const [observation, setObservation] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const categoriesQuery = useQuery({ queryKey: ["getCategories"], queryFn: getCategories, staleTime: 1000 * 60 });
    const formatsQuery = useQuery({ queryKey: ["getFormats"], queryFn: getFormats, staleTime: 1000 * 60 });
    const comfortsQuery = useQuery({ queryKey: ["getComforts"], queryFn: getComforts, staleTime: 1000 * 60 });
    const platingsQuery = useQuery({ queryKey: ["getPlatings"], queryFn: getPlatings, staleTime: 1000 * 60 });
    const finishingsQuery = useQuery({ queryKey: ["getExternalFinishings"], queryFn: getExternalFinishings, staleTime: 1000 * 60 });
    const productQuery = useQuery({ queryKey: ["getProductDetails", id], queryFn: () => getProductDetails(id), staleTime: 1000 * 60 });
    const servicesQuery = useQuery({ queryKey: ["getServices"], queryFn: getServices, staleTime: 1000 * 60 });

    const isFetching = [
        categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, finishingsQuery, productQuery, servicesQuery,
    ].some((q) => q.isFetching);

    const categories = formatOptions(categoriesQuery.data);
    const formats = formatOptions(formatsQuery.data);
    const comforts = formatOptions(comfortsQuery.data);
    const platings = formatOptions(platingsQuery.data);
    const finishings = formatOptions(finishingsQuery.data);
    const services = formatOptionsWithPrice(servicesQuery.data);

    const product = useMemo(() => productQuery.data?.data?.data ?? {}, [productQuery.data]);
    const pieceKind = getPieceKind(product);
    const needsSize = pieceKind !== "other";
    const canBePair = pieceKind === "alliance";
    const showSecond = canBePair && pair;
    const singleAlliance = canBePair && !pair;

    const price = useMemo(() => {
        const catalogPrice = Number(product.prices?.[priceByMaterial[selectedMetal]] || 0);
        const productPrice = singleAlliance ? Math.round(catalogPrice / 2) : catalogPrice;
        const extrasPrice = extras.reduce((sum, extra) => sum + Number(extra.price || 0), 0);
        const stonesPrice = stones.reduce(
            (sum, stone) => sum + Number(stone.price || 0) * Number(stone.quantity || 1),
            0,
        );
        return moneyFormat(productPrice + extrasPrice + stonesPrice);
    }, [product, selectedMetal, extras, stones, singleAlliance]);

    useEffect(() => {
        if (product?.stones) setStones(product.stones);
    }, [product]);

    useEffect(() => {
        if (pieceKind === "ring") setPair(false);
        if (pieceKind === "alliance") setPair(true);
    }, [pieceKind]);

    const handleCreateOrder = async () => {
        if (needsSize && !ringSize1) {
            appToast.error("Informe o aro.");
            return;
        }
        if (showSecond && !ringSize2) {
            appToast.error("Informe o aro da segunda aliança.");
            return;
        }
        if (!deliveryDate || !deliveryDate.isValid() || isWeekend(deliveryDate) || isOutsideDeliveryHours(deliveryDate)) {
            appToast.error("A entrega é de segunda a sexta, das 8h30 às 17h.");
            return;
        }

        setSubmitting(true);
        try {
            const response = await createOrder({
                product_id: product.id,
                selected_metal: selectedMetal,
                ring_size_one: ringSize1 ? Number(ringSize1) : null,
                ring_size_two: showSecond && ringSize2 ? Number(ringSize2) : null,
                engraving_one: engraving1,
                engraving_two: showSecond ? engraving2 : "",
                observation,
                delivery_date: deliveryDate.format("DD/MM/YYYY HH:mm"),
                stones,
                extras,
                pair: !singleAlliance,
            });

            if (response.data.success) {
                navigate("/pedido-resultado/success");
                return;
            }
            appToast.error(response.data.message || "Não foi possível criar o pedido.");
        } catch (error) {
            appToast.error(error.response?.data?.message || "Não foi possível criar o pedido.");
        } finally {
            setSubmitting(false);
            setConfirmOpen(false);
        }
    };

    if (isFetching) return <LoadingComponent />;

    const weightKey = priceByMaterial[selectedMetal].replace("_price", "_weight");
    const catalogWeight = parseWeight(product[weightKey]);
    const displayWeight = catalogWeight == null
        ? ""
        : formatGrams(singleAlliance ? catalogWeight / 2 : catalogWeight);

    return (
        <CustomContainer>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }} sx={{ py: 4 }}>
                <PageHeader
                    title="Encomendar joia"
                    crumbs={[{ label: "Meus pedidos", to: "/pedidos" }, "Encomendar"]}
                />

                <FormSection title="A joia">
                    {product.images?.length > 0 && (
                        <Box sx={styles.gallery}>
                            {product.images.map((image, idx) => (
                                <Box
                                    key={`${image}-${idx}`}
                                    component="button"
                                    type="button"
                                    onClick={() => { setImage(image); handleOpen(); }}
                                    sx={styles.thumbBtn}
                                >
                                    <Box
                                        component="img"
                                        src={image}
                                        alt={`${product.name || "Joia"} foto ${idx + 1}`}
                                        sx={styles.thumb}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Eyebrow sx={{ mb: 1 }}>{product.self_reference ? `Ref. ${product.self_reference}` : "Peça"}</Eyebrow>
                    <Typography variant="h3" sx={{ mb: 3, textAlign: "left" }}>{product.name}</Typography>
                    <SpecList columns={3}>
                        <SpecItem label="Categoria" value={product.category_name || optionLabel(categories, product.category)} />
                        <SpecItem label="Formato" value={optionLabel(formats, product.format)} />
                        <SpecItem label="Conforto" value={optionLabel(comforts, product.confort)} />
                        <SpecItem label="Altura" value={withUnit(product.height, "mm")} />
                        <SpecItem label="Largura" value={withUnit(product.width, "mm")} />
                        <SpecItem label="Espessura" value={withUnit(product.thickness, "mm")} />
                        <SpecItem label="Banho" value={optionLabel(platings, product.plating)} />
                        <SpecItem label="Acabamento" value={optionLabel(finishings, product.finishing)} />
                        <SpecItem label="Trabalhada" value={product.is_crafted ? "Sim" : "Não"} />
                        <SpecItem label="Descrição" value={product.public_description} wide />
                    </SpecList>
                </FormSection>

                <FormSection title="Metal e peso">
                    <Typography sx={styles.fieldLabel}>Material</Typography>
                    <Box sx={styles.metals}>
                        {metalList.map((material) => (
                            <ChoiceChip
                                key={material.value}
                                selected={selectedMetal === material.value}
                                onClick={() => setSelectedMetal(material.value)}
                            >
                                {material.label}
                            </ChoiceChip>
                        ))}
                    </Box>
                    <TextField
                        sx={{ mt: 3, maxWidth: 240 }}
                        label="Peso"
                        fullWidth
                        disabled
                        value={displayWeight}
                        helperText={singleAlliance ? "Uma peça. Metade do peso do par." : undefined}
                        slotProps={{ input: { endAdornment: "g" } }}
                    />
                </FormSection>

                <FormSection title="Pedras">
                    <Typography sx={styles.lede}>
                        Pedras do modelo já vêm listadas. Inclua extras só se a peça pedir.
                    </Typography>
                    {stones.length === 0 ? (
                        <Typography sx={{ color: "text.secondary", mb: 2 }}>Nenhuma pedra nesta encomenda.</Typography>
                    ) : (
                        <Box sx={styles.stoneList}>
                            {stones.map((stone, idx) => (
                                <Box key={`${stone.variation_id}-${idx}`} sx={styles.stoneRow}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={styles.stoneName}>{stone.variation_name || stone.stone_name}</Typography>
                                        <Typography sx={styles.stoneMeta}>
                                            {stone.quantity} un
                                            {stone.stone_name ? `, ${stone.stone_name}` : ""}
                                            {stone.observation ? `, ${stone.observation}` : ""}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        aria-label="Remover pedra"
                                        onClick={() => setStones((prev) => prev.filter((_, i) => i !== idx))}
                                        sx={styles.stoneRemove}
                                    >
                                        <FaTrash />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FaPlus />}
                        onClick={() => setStoneOpen(true)}
                        sx={{ mt: 2 }}
                    >
                        Incluir pedra
                    </Button>
                </FormSection>

                {needsSize && (
                    <FormSection title={pieceKind === "alliance" ? "Aro e gravação" : "Aro e gravação"}>
                        {canBePair && (
                            <>
                                <Typography sx={styles.fieldLabel}>Quantas peças</Typography>
                                <Box sx={{ ...styles.metals, mb: 3 }}>
                                    <ChoiceChip selected={!pair} onClick={() => setPair(false)}>Uma aliança</ChoiceChip>
                                    <ChoiceChip selected={pair} onClick={() => setPair(true)}>Par de alianças</ChoiceChip>
                                </Box>
                            </>
                        )}
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl required fullWidth>
                                    <InputLabel>{showSecond ? "Aro da aliança 1" : "Aro"}</InputLabel>
                                    <Select
                                        label={showSecond ? "Aro da aliança 1" : "Aro"}
                                        value={ringSize1}
                                        onChange={(e) => setRingSize1(e.target.value)}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                    >
                                        {RING_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>Aro {size}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label={showSecond ? "Gravação da aliança 1" : "Gravação"}
                                    value={engraving1}
                                    onChange={(e) => setEngraving1(e.target.value)}
                                    helperText="Opcional. Iniciais, data ou frase curta."
                                />
                            </Grid>
                            {showSecond && (
                                <>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControl required fullWidth>
                                            <InputLabel>Aro da aliança 2</InputLabel>
                                            <Select
                                                label="Aro da aliança 2"
                                                value={ringSize2}
                                                onChange={(e) => setRingSize2(e.target.value)}
                                                MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                            >
                                                {RING_SIZES.map((size) => (
                                                    <MenuItem key={size} value={size}>Aro {size}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Gravação da aliança 2"
                                            value={engraving2}
                                            onChange={(e) => setEngraving2(e.target.value)}
                                            helperText="Opcional."
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </FormSection>
                )}

                <FormSection title="Extras">
                    <Autocomplete
                        multiple
                        value={extras}
                        onChange={(_, newValue) => setExtras(newValue)}
                        options={services}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField {...params} label="Serviços extras" placeholder="Opcional" />
                        )}
                    />
                </FormSection>

                <FormSection title="Observação para a produção">
                    <TextField
                        name="observation"
                        multiline
                        rows={4}
                        label="Observação"
                        fullWidth
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        helperText="Detalhe o que a oficina precisa saber."
                    />
                </FormSection>

                <FormSection title="Entrega">
                    <Typography sx={styles.lede}>
                        Segunda a sexta, das 8h30 às 17h. O prazo mínimo é de dois dias úteis.
                    </Typography>
                    <DateTimePicker
                        value={deliveryDate}
                        onChange={setDeliveryDate}
                        shouldDisableDate={isWeekend}
                        minDate={minDate}
                        minTime={DELIVERY_MIN}
                        maxTime={DELIVERY_MAX}
                        minutesStep={15}
                        ampm={false}
                        label="Data e hora"
                        slotProps={{
                            textField: { required: true, fullWidth: true, sx: { maxWidth: 360 } },
                        }}
                    />
                </FormSection>

                <FormSection title="Valor" sx={{ mb: 3 }}>
                    <Price size="lg">{price}</Price>
                    <Typography sx={{ mt: 1.5, color: "text.secondary", maxWidth: 520 }}>
                        Este valor pode variar até 10% para mais ou para menos, conforme o peso final da joia.
                    </Typography>
                </FormSection>

                <Box sx={styles.submit}>
                    <Button
                        startIcon={<FaCartPlus />}
                        variant="contained"
                        color="secondary"
                        type="submit"
                        size="large"
                    >
                        Encomendar joia
                    </Button>
                </Box>
            </Box>

            <SelectStonesModal
                open={stoneOpen}
                handleClose={() => setStoneOpen(false)}
                handleAddStone={(stone) => setStones((prev) => [...prev, stone])}
            />
            <ConfirmDialog
                open={confirmOpen}
                title="Confirmar encomenda?"
                description="A peça entra em produção com o metal, o aro e as pedras que você escolheu."
                confirmLabel="Encomendar"
                loading={submitting}
                onCancel={() => { if (!submitting) setConfirmOpen(false); }}
                onConfirm={handleCreateOrder}
            />
        </CustomContainer>
    );
}

const styles = {
    lede: {
        mb: 2.5,
        color: "text.secondary",
        textAlign: "left",
        maxWidth: 520,
    },
    fieldLabel: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
        mb: 1,
        textAlign: "left",
    },
    metals: {
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
    },
    gallery: {
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mb: 3,
    },
    thumbBtn: {
        p: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "background.default",
        cursor: "pointer",
        "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "secondary.main",
            outlineOffset: 2,
        },
    },
    thumb: {
        display: "block",
        width: 120,
        height: 120,
        objectFit: "cover",
        aspectRatio: "1 / 1",
    },
    stoneList: {
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid",
        borderColor: "divider",
    },
    stoneRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
    },
    stoneName: {
        fontWeight: 600,
        textAlign: "left",
    },
    stoneMeta: {
        color: "text.secondary",
        fontSize: "0.8125rem",
        fontVariantNumeric: "tabular-nums",
        textAlign: "left",
    },
    stoneRemove: {
        color: "text.secondary",
        "&:hover": { color: "error.main" },
    },
    submit: {
        display: "flex",
        justifyContent: "flex-end",
    },
};
